import { useMemo } from 'react';

import { useTheme } from '@mui/material';
import { Area as RechartsArea, AreaProps as RechartsAreaProps } from 'recharts';

import { HATCHED_PATTERN_ID } from './_constants';
import { AreaProps } from './types';
import { getThemeColor } from '../../../../utils';
import { ANIMATION_DURATION_MS } from '../../_constants';
import { mapPaletteColorToDataColorToken } from '../../../../utils/mapPaletteColorToDataColorToken';
import { useChartConfig } from '../../ChartConfigProvider';
import { useChartSelection } from '../../ChartSelectionProvider';
import { ActiveDot } from '../_components/ActiveDot';
import { Dot } from '../_components/Dot';
import { useChartElementTooltip } from '../_hooks/useChartElementTooltip';
import { useGradient } from '../_hooks/useGradient';

export const Area = <T, V = string | number>({
  dataKey,
  isAnimationActive,
  fill = 'blue.500',
  fillOpacity = 1,
  stroke = 'none',
  strokeWidth = 0,
  variant = 'filled',
  type = 'monotone',
  testId = 'chart-area',
  dot = false,
  activeDot = true,
  onDotClick,
  onActiveDotClick,
  getFillByPoint,
  fillTransition = 'sharp',
  tooltipConfig,
  selectionKey,
  hide,
  ...restAreaProps
}: AreaProps<T, V>) => {
  const selection = useChartSelection();
  const hiddenBySelection =
    selectionKey !== undefined
      ? selection !== undefined && !selection.isVisible(selectionKey)
      : false;
  const theme = useTheme();
  const chartConfig = useChartConfig({ isAnimationActive });
  const semanticFill = mapPaletteColorToDataColorToken(fill, fill);
  const semanticStroke = mapPaletteColorToDataColorToken(stroke, stroke);
  const themedFill = getThemeColor(theme, semanticFill);
  const themedStroke = getThemeColor(theme, semanticStroke);
  const getStopStylesByPoint = useMemo(() => {
    if (!getFillByPoint) {
      return;
    }
    return (dataPoint: T, index: number) => {
      const styles = getFillByPoint(dataPoint, index);
      return {
        stopColor: styles?.fill
          ? getThemeColor(
              theme,
              mapPaletteColorToDataColorToken(styles.fill, styles.fill)
            )
          : themedFill,
        stopOpacity: styles?.fillOpacity ?? fillOpacity,
        transition: styles?.fillTransition,
      };
    };
  }, [getFillByPoint, theme, themedFill, fillOpacity]);
  const { gradientStops, gradientId } = useGradient(
    getStopStylesByPoint,
    fillTransition
  );
  const hasGradient = gradientStops.length > 0;
  const styledFill = hasGradient
    ? `url(#${gradientId})`
    : variant === 'hatched'
    ? `url(#${HATCHED_PATTERN_ID})`
    : themedFill;
  const styledStroke =
    stroke === 'none'
      ? // set fill for tooltip
        themedFill
      : themedStroke;
  const dotFill = variant === 'hatched' ? themedFill : styledFill;

  const internalId = useChartElementTooltip<T>({
    tooltipConfig,
    indicatorVariant:
      variant === 'hatched'
        ? 'hatched'
        : fill === 'none'
        ? 'outlined'
        : 'filled',
    getStylesByPoint: getStopStylesByPoint,
  });

  return (
    <>
      {variant === 'hatched' && (
        <defs>
          <pattern
            id={HATCHED_PATTERN_ID}
            width="8"
            height="8"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(-45)"
          >
            <path d="M 0,4 H 8" stroke={themedFill} strokeWidth="1" />
          </pattern>
        </defs>
      )}

      {hasGradient && (
        <defs>
          <linearGradient id={gradientId}>{gradientStops}</linearGradient>
        </defs>
      )}

      <RechartsArea
        {...restAreaProps}
        hide={hiddenBySelection || hide}
        dataKey={dataKey as RechartsAreaProps<T, V>['dataKey']}
        unit={internalId}
        dot={
          dot === true
            ? ({ index, key, ...rest }) => (
                <Dot
                  {...rest}
                  key={key}
                  index={index}
                  fill={dotFill}
                  testId={`${testId}-dot-${index}`}
                  onClick={onDotClick}
                />
              )
            : dot
        }
        activeDot={
          activeDot === true
            ? ({ key, ...rest }) => (
                <ActiveDot
                  {...rest}
                  key={key}
                  fill={dotFill}
                  onClick={onActiveDotClick}
                  testId={`${testId}-active-dot`}
                />
              )
            : activeDot
        }
        fill={styledFill}
        fillOpacity={fillOpacity}
        stroke={styledStroke}
        strokeWidth={strokeWidth}
        data-testid={testId}
        type={type}
        isAnimationActive={chartConfig.isAnimationActive}
        animationDuration={ANIMATION_DURATION_MS}
      />
    </>
  );
};
